import { getLocalization } from "@/data/Localization-Server";
import { useSettings } from "../Settings";
import { useMemo, useState } from "react";
import { ContentProps } from "@/data/types/ContentProps";
import { useNextRouter } from "@/data/Content/_NextRouter";
import { getClientSideCommon } from "@/data/utils/getClientSideCommon";
import { useNotifications } from "@/data/Content/Notifications";
import useSWR from "swr";
import { GetServerSidePropsContext } from "next";
import { customTransactionsReturnOrder } from "integration/generated/customTransactions";
import { OrderOrderDetail } from "integration/generated/transactions/data-contracts";
import { productFetcher } from "./_Product";
import { STORE_ID } from "../config/STORE_ID";
import { extractContentsArray } from "@/data/utils/extractContentsArray";
import { getDisplayProducts } from "../utils/returnOrder";
import { STATIC_HOST_SECURE } from "../config/STATIC_HOST_SECURE";
import { shrink } from "@/data/utils/keyUtil";
import { DisplayProduct, RegisterReturnAddressCheckForm, returnSteps } from "../types/ReturnOrder";
import { ReturnOderLabelResponse, ReturnToOmsPayload, ReturnToOmsResponse } from "integration/generated/customTransactions/data-contracts";
import { NumericDateFormatter2Digits } from "@/utils/Date";
import { getChildContentItems } from "@/data/utils/getChildContentItems";
import { getContentRecommendation } from "@/data/Content/ContentRecommendation-Server";
import { useUser } from "../User";
import { useLocalization } from "@/data/Localization";
import { TranslationTable } from "integration/generated/translations";
import { renderContent } from "@/utils/renderContent";
import { downloadPdfFromBase64 } from "@/utils/downloadFilesUtil";

export const returnAddressCheckFetcher =
	(pub: boolean, context?: GetServerSidePropsContext): any =>
		async (
			storeId: string,
			orderId: string,
			zipCode: string,
			houseNumber: string,
			catalogId: string,
			langId: string,
			currency: string
		) => {
			try {
				const order: OrderOrderDetail = await customTransactionsReturnOrder(pub).returnOrderAddressCheck(
					storeId,
					orderId,
					zipCode,
					houseNumber,
				);
				const catalogEntryIds = order?.orderItem?.map(o => o.xitem_parentCatalogEntryId) ?? [];
				const query = {
					storeId,
					id: catalogEntryIds,
					catalogId,
					langId,
					currency,
				}
				const response = await productFetcher(pub, context)(query, {});
				const products = extractContentsArray(response);
				return { order, products };
			} catch (error: any) {
				return error;
			}
		};

export const submitReturnsToOMSFetcher =
	(pub: boolean): ReturnToOmsResponse | any =>
		async (
			storeId: string,
			orderId: string,
			returnToOMSPayload: ReturnToOmsPayload,
		) => {
			try {
				return await customTransactionsReturnOrder(pub).returnOrderSubmitToOms(
					storeId,
					orderId,
					returnToOMSPayload,
					{},
					{}
				);
			} catch (error: any) {
				return error;
			}
		};

export const downloadReturnLabelFetcher =
	(pub: boolean): ReturnOderLabelResponse | any =>
		async (
			storeId: string,
			orderId: string,
			barCode:string
		) => {
			try {
				const response = await customTransactionsReturnOrder(pub).returnOrderDownloadReturnLabel(
					storeId,
					orderId,
					barCode,
					{},
					{}
				);
				return response
			} catch (error: any) {
				return error;
			}
		};

export const getReturnRegister = async ({ cache, id, context, properties }: ContentProps) => {
	await Promise.all([
			getLocalization(cache, context.locale || 'nl-NL', 'ReturnOrder'),
			...getChildContentItems(id).map((contentProperties) =>
				getContentRecommendation({
					cache,
					id,
					context,
					properties: { ...properties, ...contentProperties },
				})
			),
		]);
}

export const useReturnRegister = () => {

	const initReturnAddressValues: RegisterReturnAddressCheckForm = useMemo(()=>{
		return {
			orderNumber: '',	
			postCode: '',
			houseNumber: ''
		}
	},[]);
	
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, defaultCatalogId: catalogId, defaultCurrency: currency, langId} = getClientSideCommon(settings, router);
	const staticHostSecure = useMemo(()=>settings?.userData?.StaticHostSecure??STATIC_HOST_SECURE,[settings]);

	const { showErrorMessage } = useNotifications();

	const [activeReturnStep, setActiveReturnStep] = useState(returnSteps.addressCheck);

	const [returnAddressValues, setReturnAddressValues] = useState<RegisterReturnAddressCheckForm>(initReturnAddressValues);
	const [displayProducts, setDisplayProducts] = useState<DisplayProduct[]>([]);
	const [selectedItems, setSelectedItems] = useState<{id:string;quantity:number}[]>([]);
	const [selectedReturnReason, setSelectedReturnReason] = useState("");
	const [omsResponse,setOMSResponse] = useState<ReturnToOmsResponse>({} as ReturnToOmsResponse);

	const { user: { buyerAdmin = false} = {} } = useUser();

	const retErrNLS = useLocalization("ReturnOrder").Errors
	
	const checkReturnOrderValid = async(returnAddressValues:RegisterReturnAddressCheckForm) => {
		const response =  await returnAddressCheckFetcher(true)(storeId ?? STORE_ID, 
				returnAddressValues.orderNumber, 
				returnAddressValues.postCode.replaceAll(" ",""),
				returnAddressValues.houseNumber,
				catalogId,
				langId,
				currency
			);

			if(response?.error){
				let errorKey:keyof TranslationTable['ReturnOrder']['Errors'] = "_ERR_NO_ORDER_FOUND_FOR_RETURN_PROCESSING";
				if(response?.error?.error)errorKey = response?.error?.error;
				if(errorKey==='_ERR_STORE_PICKUP_RETURNS_UNAVAILABLE'){
					showErrorMessage([
						renderContent({
							text:`${retErrNLS['_ERR_STORE_PICKUP_RETURNS_UNAVAILABLE'].t()}&nbsp;&nbsp;`
						}),
						renderContent({
							contentUrl:"/klantenservice/ruilen-retourneren",
							text:"ruilen en retourneren."
						})
					])
				}else{
					showErrorMessage(retErrNLS[errorKey].t());
				}
			}else{
				setReturnAddressValues(returnAddressValues);
				setActiveReturnStep(returnSteps.selectProduct);
				if(response?.order && response?.products){
					const displayProducts = getDisplayProducts(response.order, response.products, staticHostSecure);
					setDisplayProducts(displayProducts);
				}
			}
	}

	const subitReturnToOMS = async() => {
		const orderDateFormatted = NumericDateFormatter2Digits({dateString:displayProducts?.at(0)?.orderPlacedDate??""})
		const orderDate = orderDateFormatted?.split("-")?.reverse()?.join("");
		const returnToOMSPayload: ReturnToOmsPayload = {
			orderId:returnAddressValues.orderNumber,
			orderPlacedDate:orderDate,
			lines:selectedItems.map(si=>{
				const dp = displayProducts.find(dp=>dp.id===si.id) as DisplayProduct;
				return{
					product_identifier:`${dp.id}###${dp.partNumber}`,
					product_state:'sellable',
					quantity:String(si.quantity),
					return_reason:selectedReturnReason
				}
			})
		} as ReturnToOmsPayload;
		const response = await submitReturnsToOMSFetcher(true)(
			storeId ?? STORE_ID,
			returnAddressValues.orderNumber,
			returnToOMSPayload
		);
		if(response?.error?.error){
			const errorKey:keyof TranslationTable['ReturnOrder']['Errors'] = response?.error?.error;
			showErrorMessage(retErrNLS[errorKey].t());
		}else{
			if(response?.returnAnnounced){
				setActiveReturnStep(returnSteps.downloadReturnForm);
				response.orderId = returnAddressValues.orderNumber;
				setOMSResponse(response);
			}else{
				setActiveReturnStep(returnSteps.errorPage);
			}
		}
		
	}
	const  downloadReturnLabel = async( ) => {
		const response = await downloadReturnLabelFetcher(true)(
			storeId ?? STORE_ID,
			omsResponse.orderId,
			omsResponse.barCode
		)
		if(response?.error){
			const errorKey:keyof TranslationTable['ReturnOrder']['Errors'] = response?.error?.error;
			showErrorMessage(retErrNLS[errorKey].t());
		}else{
			if(response?.labelGenerated){
				downloadPdfFromBase64(response?.labelContent, response?.fileName);
			}else{
				setActiveReturnStep(returnSteps.errorPage);
			}
		}
	}	

	return {
		returnSteps,
		activeReturnStep,
		setActiveReturnStep,
		checkReturnOrderValid,		
		subitReturnToOMS, 
		downloadReturnLabel,
		initReturnAddressValues,
		returnAddressValues,
		setReturnAddressValues,
		displayProducts,
		selectedItems,
		setSelectedItems,
		selectedReturnReason,
		setSelectedReturnReason,
		buyerAdmin,
		staticHostSecure
	};
};

export const returnReasonsFetcher =
	(pub: boolean): any =>
		async (
			storeId: string,
			langId: string,
		) => {
			try {
				return await customTransactionsReturnOrder(pub).returnOrderReasons(
					storeId,
					{langId},
					{}
				);
			} catch (error: any) {
				return error;
			}
		};

export const useReturnReasons = () => {
	const DATA_KEY = 'RETURN_REASONS';
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const { data } = useSWR(
		langId?[shrink({langId}),DATA_KEY]:null, async()=>returnReasonsFetcher(true)(storeId??STORE_ID, langId)
	);
	return {reasons:data??{}}
}